<template>
  <main>
    <div class="row">
      <!-- LEFT SIDE START -->
      <div class="col-12 col-lg-3">
        <!-- CRITICAL BACKUP START -->
        <section name="critical-backup" class="h-100 position-relative">
          <div class="critical__bar rounded-top"></div>
          <b-card class="h-100">
            <header class="text__header my-2 card__container-x">
              Critical Backup
            </header>
            <main
              v-if="criticalBackupList.length"
              class="content__container padding-x"
            >
              <div v-for="(backup, index) in criticalBackupList" :key="index">
                <div class="w-100 d-flex justify-content-between">
                  <div class="d-flex flex-column">
                    <div class="d-flex align-items-center">
                      <label class="text__topic mb-0">{{ backup.name }}</label>
                      <div
                        :id="'description-tooltip' + backup.id + backup.name"
                        class="d-flex"
                      >
                        <feather-icon
                          icon="AlertCircleIcon"
                          size="12"
                          class="alert__icon"
                        />
                      </div>
                      <b-tooltip
                        :target="
                          'description-tooltip' + backup.id + backup.name
                        "
                        placement="right"
                        boundary="window"
                        variant="primary"
                      >
                        <div class="description__container">
                          {{ backup.description }}
                        </div>
                      </b-tooltip>
                    </div>
                    <label
                      v-for="(ip, index) in backup.ipAddress"
                      :key="index"
                      class="text__sub-topic mb-0"
                      >{{ ip }}</label
                    >
                    <div class="mt-1">
                      <b-badge variant="light-primary" class="rounded-pill">
                        {{ backup.veeamRef }}
                      </b-badge>
                    </div>
                  </div>
                  <div class="d-flex flex-column align-items-end">
                    <label class="mb-0">{{ backup.eventDate.date }}</label>
                    <label class="mb-0">{{ backup.eventDate.time }}</label>
                    <div class="mt-1">
                      <feather-icon
                        :icon="getIconFromStatus(backup.status)"
                        size="24"
                        :class="getColorIconFromStatus(backup.status)"
                      />
                    </div>
                  </div>
                </div>
                <hr />
              </div>
            </main>
            <main v-else class="card__container">
              <p class="text__no-data">
                No critical backup for the last 24 hours
              </p>
            </main>
          </b-card>
        </section>
        <!-- CRITICAL BACKUP END -->
      </div>
      <!-- LEFT SIDE END -->

      <!-- RIGHT SIDE START -->
      <div class="col-12 col-lg-9 pl-lg-0 mt-1 mt-lg-0">
        <div class="row">
          <div class="col-12">
            <!-- VEEAM STATUS START -->
            <section name="veeam-status">
              <b-card class="mb-1">
                <header class="text__header my-2 card__container-x">
                  Veeam Status
                </header>
                <main v-if="veeamStatusList.length" class="content__container">
                  <b-table
                    responsive
                    :items="veeamStatusList"
                    :fields="veeamStatusFields"
                    :hover="false"
                    sticky-header="310px"
                  >
                    <template #cell(name)="data">
                      <label class="text__topic">{{ data.value }}</label>
                    </template>
                    <template #cell(ipAddress)="data">
                      <label class="text__topic">{{ data.value }}</label>
                    </template>
                    <template #cell(lastStatus)="data">
                      <div class="row box__container">
                        <div class="col-4 text-center pl-0">
                          <label class="mb-0 text-success font-10px"
                            >Success</label
                          >
                          <h6 class="mb-0 text-success">
                            {{ data.value.success }}
                          </h6>
                        </div>
                        <div class="col-4 text-center p-0">
                          <label class="mb-0 text-warning font-10px"
                            >Warning</label
                          >
                          <h6 class="mb-0 text-warning">
                            {{ data.value.warning }}
                          </h6>
                        </div>
                        <div class="col-4 text-center pr-0">
                          <label class="mb-0 text-danger font-10px"
                            >Critical</label
                          >
                          <h6 class="mb-0 text-danger">
                            {{ data.value.critical }}
                          </h6>
                        </div>
                      </div>
                    </template>
                    <template #cell(description)="data">
                      <label class="text__topic">{{ data.value }}</label>
                    </template>
                  </b-table>
                </main>
                <main v-else class="content__container">
                  <p class="text__no-data">Please add Veeam server first.</p>
                </main>
              </b-card>
            </section>
            <!-- VEEAM STATUS END -->
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-lg-4">
            <!-- LATEST SUCCESSFUL BACKUP START -->
            <section name="latest-successful-backup">
              <b-card class="mb-1 mb-lg-0">
                <header class="text__header my-2 card__container-x">
                  Latest Successful Backup
                </header>
                <main
                  v-if="latestSuccessfulBackupList.length"
                  class="content__container padding-x"
                >
                  <div
                    v-for="(backup, index) in latestSuccessfulBackupList"
                    :key="index"
                  >
                    <div class="w-100 d-flex justify-content-between">
                      <div class="d-flex flex-column">
                        <label class="text__topic mb-0">{{
                          backup.name
                        }}</label>
                        <label
                          v-for="(ip, index) in backup.ipAddress"
                          :key="index"
                          class="text__sub-topic mb-0"
                          >{{ ip }}</label
                        >
                        <div class="mt-1">
                          <b-badge variant="light-primary" class="rounded-pill">
                            {{ backup.veeamRef }}
                          </b-badge>
                        </div>
                      </div>
                      <div class="d-flex flex-column align-items-end">
                        <label class="mb-0">{{ backup.eventDate.date }}</label>
                        <label class="mb-0">{{ backup.eventDate.time }}</label>
                      </div>
                    </div>
                    <hr />
                  </div>
                </main>
                <main v-else class="content__container padding-x">
                  <p class="text__no-data">
                    No latest successful backup for the last 24 hours
                  </p>
                </main>
              </b-card>
            </section>
            <!-- LATEST SUCCESSFUL BACKUP END -->
          </div>

          <div class="col-12 col-lg-4 pl-lg-0">
            <!-- NEXT 6 BACKUP START -->
            <section name="next-6-backup">
              <b-card class="mb-1 mb-lg-0">
                <header class="text__header my-2 card__container-x">
                  Next 6 Backup
                </header>
                <main
                  v-if="next6BackupList.length"
                  class="content__container padding-x"
                >
                  <div v-for="(backup, index) in next6BackupList" :key="index">
                    <div class="w-100 d-flex justify-content-between">
                      <div class="d-flex flex-column">
                        <label class="text__topic mb-0">{{
                          backup.name
                        }}</label>
                        <label
                          v-for="(ip, index) in backup.ipAddress"
                          :key="index"
                          class="text__sub-topic mb-0"
                          >{{ ip }}</label
                        >
                        <div class="mt-1">
                          <b-badge variant="light-primary" class="rounded-pill">
                            {{ backup.veeamRef }}
                          </b-badge>
                        </div>
                      </div>
                      <div class="d-flex flex-column align-items-end">
                        <label class="mb-0">{{ backup.eventDate.date }}</label>
                        <label class="mb-0">{{ backup.eventDate.time }}</label>
                      </div>
                    </div>
                    <hr />
                  </div>
                </main>
                <main v-else class="content__container padding-x">
                  <p class="text__no-data">
                    No next 6 backup for the last 24 hours
                  </p>
                </main>
              </b-card>
            </section>
            <!-- NEXT 6 BACKUP END -->
          </div>

          <div class="col-12 col-lg-4 pl-lg-0">
            <!-- STORAGE USAGE START -->
            <section name="storage-usage">
              <b-card class="mb-1 mb-lg-0">
                <header class="text__header my-2 card__container-x">
                  Storage usage
                </header>
                <main
                  v-if="storageUsageList.length"
                  class="content__container padding-x"
                >
                  <div
                    v-for="(storage, index) in storageUsageList"
                    :key="index"
                    class="mb-1"
                  >
                    <div class="w-100 d-flex flex-column">
                      <label class="mb-0 text__topic">{{ storage.name }}</label>
                      <label class="text__sub-topic">{{
                        storage.veeamRef
                      }}</label>
                    </div>
                    <div class="d-flex w-100 h-100 align-items-center">
                      <div class="progress__size">
                        <atom-bootstrap-progress-bar
                          color="primary"
                          :value="parseFloat(storage.usage)"
                          
                        ></atom-bootstrap-progress-bar>
                      </div>
                      <div class="percent__width d-flex justify-content-end">
                        <span
                          class="text--extra-bold text--normal-size text-primary"
                        >
                          {{ storage.usage }} %</span
                        >
                      </div>
                    </div>
                  </div>
                </main>
                <main v-else class="content__container padding-x">
                  <p class="text__no-data">No storage repository</p>
                </main>
              </b-card>
            </section>
            <!-- STORAGE USAGE END -->
          </div>
        </div>
      </div>
      <!-- RIGHT SIDE END -->
    </div>
  </main>
</template>

<script>
import { BCard, BBadge, BTooltip, BTable } from "bootstrap-vue"
import AtomBootstrapProgressBar from "@/components/atoms/graphs/AtomBootstrapProgressBar.vue"

export default {
  name: "OrganismBackupDashboard",
  components: { BCard, BBadge, BTooltip, BTable, AtomBootstrapProgressBar },
  props: {
    // critical backup
    criticalBackupList: {
      type: Array,
      required: true,
    },

    // veeam status
    veeamStatusFields: {
      type: Array,
      required: true,
    },
    veeamStatusList: {
      type: Array,
      required: true,
    },
    latestSuccessfulBackupList: {
      type: Array,
      required: true,
    },
    next6BackupList: {
      type: Array,
      required: true,
    },
    storageUsageList: {
      type: Array,
      required: true,
    },
  },
  emits: [],
  data() {
    return {}
  },
  mounted() {},
  methods: {
    getIconFromStatus(status) {
      if (status === "Warning") return "AlertTriangleIcon"
      else if (status === "Failed") return "XIcon"
    },
    getColorIconFromStatus(status) {
      if (status === "Warning") return "text-warning"
      else if (status === "Failed") return "text-danger"
      else return "text-primary"
    },
  },
}
</script>

<style lang="scss" scoped>
.text__header {
  color: #5e5873;
  font-weight: 600;
  font-size: 14px;
}

.text__topic {
  color: #6e6b7b;
  font-weight: 500;
  font-size: 12px;
}

.text__sub-topic {
  color: #b9b9c3;
  font-weight: 400;
  font-size: 10px;
}

.text__no-data {
  color: #909399;
  width: 100%;
  display: flex;
  justify-content: center;
}

.font-10px {
  font-size: 10px;
}

hr {
  margin-top: 1rem;
  margin-bottom: 1rem;
  border: 0;
  border-top: 1px solid #ebe9f1;
}

.card-body {
  padding: 0;
}

.card__container {
  padding: 1.5rem;
}

.card__container-x {
  padding: 0 1.5rem;
}

.alert__icon {
  margin-left: 3px;
}

.content__container {
  // height: 300px;
  max-height: 300px;
  overflow-y: auto;
}

.padding-x {
  padding: 0 1.5rem;
}

.box__container {
  background: #ffffff;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  padding: 4px 8px;
  width: 200px;
}

section[name="critical-backup"] {
  .critical__bar {
    background: #ea5455;
    position: absolute;
    width: 100%;
    height: 0.5rem;
    z-index: 1;
  }

  // Large devices (desktops, 992px and up)
  @media screen and (min-width: 992px) {
    .content__container {
      height: calc(100vh - 21rem);
      max-height: calc(100vh - 21rem);
      overflow-y: auto;
    }
  }
}


.progress__size {
  width: calc(100% - 50px);
  margin-right: 0.5rem;
}

.percent__width {
  width: 50px;
}

.text--extra-bold {
  font-weight: 700;
}

.text--normal-size {
  font-size: 12px;
}

section[name="veeam-status"] {
  // Large devices (desktops, 992px and up)
  @media screen and (min-width: 992px) {
    .content__container {
      height: calc(100vh - 19rem - 60px - (100vh - 23rem - 320px));
      max-height: calc(100vh - 19rem - 60px - (100vh - 23rem - 320px));
      overflow-y: auto;
    }
  }
}

section[name="latest-successful-backup"],
section[name="next-6-backup"],
section[name="storage-usage"] {
  // Large devices (desktops, 992px and up)
  @media screen and (min-width: 992px) {
    .content__container {
      height: calc(100vh - 23rem - 320px);
      max-height: calc(100vh - 23rem - 320px);
      overflow-y: auto;
    }
  }
}

::v-deep .progress {
  height: 10px;
}
</style>
